import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Activity, TrendingUp, Plus } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import { CountUp } from "@/components/motion/CountUp";
import { SpotlightCard } from "@/components/motion/SpotlightCard";
import { howWeWork, whatWeDo, solutions, industries, whyUs, engagement, projects } from "@/data/content";

const ABOUT_IMG = "https://images.unsplash.com/photo-1607694434367-12adf196f305?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbWluaW1hbCUyMGFyY2hpdGVjdHVyZSUyMGludGVyaW9yfGVufDB8fHxibGFja3wxNzgyMjEyNjkzfDA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const ease = [0.16, 1, 0.3, 1];

const Eyebrow = ({ children }) => <p className="eyebrow">{children}</p>;

const marqueeItems = [
  "Operational Audits", "Lead Routing", "AI Assistants", "Custom Software", "CRM Sync", "KPI Dashboards",
  "Document Processing", "Ticket Routing", "Forecasting", "Approval Workflows", "Knowledge Search", "ERP Integration",
];

const DashboardPreview = () => {
  const bars = [42, 58, 36, 72, 50, 88, 64, 96, 70, 82, 60, 90];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.15, ease }}
      className="gp-float rounded-lg border border-[#1e1e1e] bg-[#141414] p-5 shadow-[0_30px_90px_-40px_rgba(21,179,81,0.5)]"
    >
      <div className="flex items-center justify-between border-b border-[#1e1e1e] pb-4">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#15b351]/15 text-[#15b351]">
            <Activity className="h-4 w-4" />
          </span>
          <span className="text-sm font-medium text-white">Operations Overview</span>
        </div>
        <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[#15b351]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#15b351] gp-blink" /> Live
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 py-4">
        {[
          { l: "Throughput", to: 12.4, suffix: "k", dec: 1, d: "+18%" },
          { l: "Cycle Time", to: 3.2, suffix: "h", dec: 1, d: "-24%" },
          { l: "Automated", to: 86, suffix: "%", dec: 0, d: "+9%" },
        ].map((s) => (
          <div key={s.l} className="rounded-md border border-[#1e1e1e] bg-[#0a0a0a] p-3">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#7c7c7c]">{s.l}</p>
            <CountUp to={s.to} suffix={s.suffix} decimals={s.dec} className="mt-1.5 block text-lg font-semibold text-white" />
            <p className="mt-0.5 text-[11px] text-[#15b351]">{s.d}</p>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-[#1e1e1e] bg-[#0a0a0a] p-4">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#7c7c7c]">Workflow Volume</span>
          <span className="flex items-center gap-1 text-[11px] text-[#15b351]"><TrendingUp className="h-3 w-3" /> Trending up</span>
        </div>
        <div className="relative mt-4 h-28">
          <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 40">
            <polyline className="gp-draw" points="0,30 12,24 24,28 36,16 48,20 60,8 72,14 84,6 100,10"
              fill="none" stroke="#15b351" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
          </svg>
          <div className="absolute inset-0 flex items-end gap-1.5">
            {bars.map((h, i) => (
              <div
                key={i}
                className="gp-bar flex-1 rounded-sm"
                style={{
                  height: `${h}%`,
                  animationDelay: `${0.2 + i * 0.05}s`,
                  background: i === 7 ? "#15b351" : "rgba(21,179,81,0.22)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden bg-[#0a0a0a] pt-16">
    <div className="absolute inset-0 gp-blueprint gp-radial-fade" />
    <div className="gp-aurora-a absolute -top-20 left-1/4 h-[420px] w-[620px] rounded-full bg-[#15b351]/12 blur-[130px]" />
    <div className="gp-aurora-b absolute top-40 right-0 h-[360px] w-[480px] rounded-full bg-[#15b351]/8 blur-[120px]" />
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-[#1e1e1e] bg-[#141414] px-3 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#15b351] gp-blink" />
            <span className="font-mono text-[11px] uppercase tracking-[0.085em] text-[#a7a7a7]">Business Systems · AI · Automation</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.06, ease }}
            className="mt-7 text-[44px] sm:text-5xl lg:text-[64px] font-semibold tracking-[-0.035em] leading-[1.04] text-white"
          >
            Transform Your Business Operations with <span className="text-[#15b351]">AI</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.14, ease }}
            className="mt-7 text-lg text-[#a7a7a7] leading-relaxed max-w-xl"
          >
            Graphikpro helps businesses identify operational bottlenecks, build custom software, deploy AI solutions,
            and automate workflows that drive measurable results.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22, ease }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-consultation-btn"
              onClick={() => scrollTo("contact")}
              className="gp-shine inline-flex items-center justify-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-medium text-[#0a0a0a] hover:bg-[#15b351] hover:text-white transition-colors"
            >
              Book a Consultation <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="hero-solutions-btn"
              onClick={() => scrollTo("services")}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#454545] px-6 py-3.5 text-sm font-medium text-white hover:border-[#15b351] hover:bg-[#15b351]/5 transition-colors"
            >
              Explore Solutions <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-10 text-sm text-[#7c7c7c] leading-relaxed max-w-lg border-l border-[#313131] pl-4"
          >
            From strategy to implementation, we design and build systems that reduce manual work, improve visibility,
            increase productivity, and enable scalable growth.
          </motion.p>
        </div>

        <div className="lg:col-span-6">
          <DashboardPreview />
        </div>
      </div>
    </div>

    <div className="gp-marquee relative border-y border-[#1e1e1e] bg-[#0a0a0a]/60 py-4 overflow-hidden">
      <div className="gp-marquee-track">
        {[...marqueeItems, ...marqueeItems].map((it, i) => (
          <span key={i} className="mx-6 flex items-center gap-3 font-mono text-xs uppercase tracking-wider text-[#7c7c7c]">
            {it} <span className="h-1 w-1 rounded-full bg-[#15b351]/60" />
          </span>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0a0a0a] to-transparent" />
    </div>
  </section>
);

const SectionHead = ({ eyebrow, title, sub }) => (
  <Reveal className="max-w-2xl">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-5 text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white">{title}</h2>
    {sub && <p className="mt-5 text-base text-[#a7a7a7]">{sub}</p>}
  </Reveal>
);

const HowWeWork = () => (
  <section id="how-we-work" data-testid="how-we-work" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <SectionHead eyebrow="Trusted Approach" title="How We Work" sub="A repeatable, transparent process — from understanding your operations to continuously optimizing the systems we build." />

      <div className="relative mt-14">
        <div className="hidden lg:block absolute top-[58px] left-[6%] right-[6%] h-px bg-[#1e1e1e] overflow-hidden">
          <span className="gp-flow-dot absolute -top-[2px] h-1.5 w-16 rounded-full bg-gradient-to-r from-transparent via-[#15b351] to-transparent" />
        </div>
        <RevealGroup className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#1e1e1e] rounded-lg overflow-hidden border border-[#1e1e1e]">
          {howWeWork.map((s, i) => {
            const Icon = s.icon;
            return (
              <RevealItem key={s.n}>
                <SpotlightCard testid={`how-step-${i + 1}`} className="h-full bg-[#0a0a0a] p-6 hover:bg-[#141414] transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#141414] border border-[#1e1e1e] text-[#15b351] transition-all group-hover:border-[#15b351]/50 group-hover:shadow-[0_0_18px_-4px_rgba(21,179,81,0.6)]">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <span className="font-mono text-xs text-[#454545]">{s.n}</span>
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-white">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#7c7c7c]">{s.text}</p>
                </SpotlightCard>
              </RevealItem>
            );
          })}
        </RevealGroup>
      </div>
    </div>
  </section>
);

const WhatWeDo = () => (
  <section id="services" data-testid="what-we-do" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <SectionHead eyebrow="What We Do" title="Capabilities built for outcomes" sub="Consulting, automation, AI, and custom software — combined to solve real business problems end to end." />
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1e1e1e] rounded-lg overflow-hidden border border-[#1e1e1e]">
        {whatWeDo.map((s, i) => {
          const Icon = s.icon;
          return (
            <RevealItem key={s.title}>
              <SpotlightCard testid={`service-${i}`} className="h-full bg-[#0a0a0a] p-7 hover:bg-[#141414] transition-colors">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#141414] border border-[#1e1e1e] text-[#15b351] transition-all group-hover:border-[#15b351]/50 group-hover:shadow-[0_0_18px_-4px_rgba(21,179,81,0.6)]">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 text-xl font-semibold tracking-tight text-white">{s.title}</h3>
                <p className="mt-2.5 text-sm text-[#a7a7a7] leading-relaxed">{s.text}</p>
                <ul className="mt-5 space-y-2">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-center gap-2 text-sm text-[#7c7c7c]">
                      <Check className="h-3.5 w-3.5 text-[#15b351] shrink-0" strokeWidth={3} />
                      {it}
                    </li>
                  ))}
                </ul>
              </SpotlightCard>
            </RevealItem>
          );
        })}
      </RevealGroup>
    </div>
  </section>
);

const Solutions = () => (
  <section id="solutions" data-testid="solutions" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <SectionHead eyebrow="Solutions" title="Solutions by function" sub="Targeted systems that improve performance across every department." />
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {solutions.map((s, i) => {
          const Icon = s.icon;
          const wide = i === 0 || i === 3;
          return (
            <RevealItem key={s.title} className={wide ? "lg:col-span-2" : ""}>
              <SpotlightCard testid={`solution-${i}`} className="h-full rounded-lg border border-[#1e1e1e] bg-[#141414] p-7 transition-colors hover:border-[#15b351]/40">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#0a0a0a] border border-[#1e1e1e] text-[#15b351]">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="text-xl font-semibold tracking-tight text-white">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-[#a7a7a7]">{s.text}</p>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.items.map((it) => (
                    <span key={it} className="rounded-md font-mono text-[11px] uppercase tracking-wide text-[#a7a7a7] bg-[#0a0a0a] border border-[#1e1e1e] px-2.5 py-1.5 transition-colors hover:border-[#15b351]/40 hover:text-white">{it}</span>
                  ))}
                </div>
              </SpotlightCard>
            </RevealItem>
          );
        })}
      </RevealGroup>
    </div>
  </section>
);

const Industries = () => (
  <section id="industries" data-testid="industries" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <SectionHead eyebrow="Industry Expertise" title="Industries we serve" sub="Deep operational experience across the sectors where systems thinking matters most." />
      <RevealGroup stagger={0.05} className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1e1e1e] rounded-lg overflow-hidden border border-[#1e1e1e]">
        {industries.map((ind, i) => {
          const Icon = ind.icon;
          return (
            <RevealItem key={ind.title}>
              <SpotlightCard testid={`industry-${i}`} className="h-full bg-[#0a0a0a] p-6 hover:bg-[#141414] transition-colors">
                <Icon className="h-7 w-7 text-[#15b351] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-110" strokeWidth={1.5} />
                <h3 className="mt-4 text-base font-semibold text-white">{ind.title}</h3>
                <p className="mt-2 text-sm text-[#7c7c7c] leading-relaxed">{ind.text}</p>
              </SpotlightCard>
            </RevealItem>
          );
        })}
      </RevealGroup>
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-us" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <Eyebrow>Why Graphikpro</Eyebrow>
          <h2 className="mt-5 text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white">A partner, not a vendor</h2>
          <p className="mt-6 text-base text-[#a7a7a7] leading-relaxed">We believe technology should serve business outcomes — not the other way around.</p>
        </Reveal>
        <RevealGroup className="lg:col-span-8">
          {whyUs.map((w, i) => (
            <RevealItem key={w.title}>
              <div data-testid={`why-${i}`} className="grid grid-cols-1 sm:grid-cols-12 gap-3 py-6 border-b border-[#1e1e1e] first:border-t group transition-colors hover:bg-[#141414]/40 px-2 -mx-2 rounded">
                <div className="sm:col-span-1 font-mono text-xs text-[#454545] pt-1 transition-colors group-hover:text-[#15b351]">0{i + 1}</div>
                <h3 className="sm:col-span-4 text-2xl font-semibold tracking-tight text-white transition-colors group-hover:text-[#15b351]">{w.title}</h3>
                <p className="sm:col-span-7 text-base text-[#a7a7a7] self-center">{w.text}</p>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </div>
  </section>
);

const OurProjects = () => (
  <section id="projects" data-testid="our-projects" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <SectionHead eyebrow="Our Projects" title="Systems we've shipped" sub="Real platforms built end to end — from operational chaos to production-grade software." />
      <RevealGroup className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {projects.map((p, i) => (
          <RevealItem key={p.slug}>
            <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="block h-full">
              <SpotlightCard className="h-full rounded-2xl border border-[#1e1e1e] bg-[#141414] overflow-hidden transition-colors hover:border-[#15b351]/40">
                <div className="relative aspect-[16/10] overflow-hidden border-b border-[#1e1e1e]">
                  <img src={p.cover} alt={p.name} className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1.2s] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full border border-[#15b351]/30 bg-[#0a0a0a]/80 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#15b351] backdrop-blur">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#15b351] gp-blink" /> {p.status}
                  </span>
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[#7c7c7c]">{p.category}</p>
                    <span className="font-mono text-[11px] text-[#454545]">{p.year}</span>
                  </div>
                  <h3 className="mt-3 text-2xl font-semibold tracking-tight text-white">{p.name}</h3>
                  <p className="mt-2.5 text-sm text-[#a7a7a7] leading-relaxed">{p.tagline}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.tags.slice(0, 5).map((t) => (
                      <span key={t} className="rounded-md font-mono text-[10px] uppercase tracking-wide text-[#a7a7a7] bg-[#0a0a0a] border border-[#1e1e1e] px-2 py-1">{t}</span>
                    ))}
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white transition-colors group-hover:text-[#15b351]">
                    View case study <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </SpotlightCard>
            </Link>
          </RevealItem>
        ))}

        <RevealItem>
          <div data-testid="project-coming-soon" className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#1e1e1e] bg-[#0a0a0a] p-10 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-md border border-[#1e1e1e] bg-[#141414] text-[#15b351]">
              <Plus className="h-5 w-5" />
            </span>
            <h3 className="mt-4 text-lg font-semibold text-white">More projects on the way</h3>
            <p className="mt-2 max-w-xs text-sm text-[#7c7c7c]">We're adding case studies one by one. Have a system you want built next?</p>
            <button data-testid="project-coming-soon-cta" onClick={() => scrollTo("contact")} className="mt-5 text-sm font-medium text-[#15b351] hover:underline">
              Start a project →
            </button>
          </div>
        </RevealItem>
      </RevealGroup>
    </div>
  </section>
);

const Engagement = () => (
  <section id="engagement" data-testid="engagement" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <SectionHead eyebrow="Engagement Model" title="How we engage" sub="A clear path from first conversation to long-term optimization." />
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
        {engagement.map((e, i) => (
          <RevealItem key={e.title}>
            <div data-testid={`engagement-${i}`} className="relative md:pt-8 group">
              <div className="hidden md:block absolute top-[11px] left-0 right-0 h-px bg-[#1e1e1e]" />
              <span className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full bg-[#15b351] text-black font-mono text-[11px] gp-node">{i + 1}</span>
              <h3 className="mt-5 text-base font-semibold text-white transition-colors group-hover:text-[#15b351]">{e.title}</h3>
              <p className="mt-2 text-sm text-[#7c7c7c] leading-relaxed">{e.text}</p>
            </div>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const About = () => (
  <section id="about" data-testid="about" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Eyebrow>About Graphikpro</Eyebrow>
          <h2 className="mt-5 text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white">
            Systems thinking for sustainable growth
          </h2>
          <p className="mt-7 text-base text-[#a7a7a7] leading-relaxed">
            Graphikpro combines business consulting, software development, workflow automation, and artificial
            intelligence to help organizations transform how they operate.
          </p>
          <p className="mt-5 text-base text-[#a7a7a7] leading-relaxed">
            Our team works closely with leadership teams to identify challenges, design solutions, and deliver
            systems that support sustainable growth.
          </p>
          <p className="mt-5 text-base font-medium text-white">
            We believe technology should serve business outcomes — not the other way around.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-lg border border-[#1e1e1e] overflow-hidden group">
            <img src={ABOUT_IMG} alt="Architectural corridor" className="w-full object-cover max-h-[440px] transition-transform duration-[1.2s] group-hover:scale-105" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="bg-[#0a0a0a]">
      <Navbar />
      <main>
        <Hero />
        <HowWeWork />
        <WhatWeDo />
        <Solutions />
        <Industries />
        <WhyUs />
        <OurProjects />
        <Engagement />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
